import {
  TWO_COLUMN_WORKSPACE,
  type BlockManifest,
} from '../../../../projects/template-ui/src/lib/blocks/block-contract';
import { domainBlockManifests } from '../../phms/workflow-blocks/domain-block-manifests';
import { votingBlockManifests } from '../workflow-voting/voting-block-manifests';

import { schedulingBlockManifests } from '../../phms/workflow-blocks/scheduling-block-manifests';
import { hearingCompletionManifests } from '../../phms/workflow-blocks/hearing-completion-manifests';
import { documentsBlock } from './workflow-documents-block-manifests';
import { caseSnapshotBlock } from '../../phms/workflow-blocks/case-snapshot-block-manifests';
export const guidanceBlock: BlockManifest = {
  provider: 'platform',
  code: 'workflow.guidance',
  version: 1,
  name: 'Staff guidance',
  description:
    'Instructions saved with this workflow version. Plain text only; guidance does not add completion requirements.',
  category: 'Guidance',
  keywords: ['instructions', 'help'],
  contextNeeds: [],
  reads: [],
  settingsSchema: {
    code: 'workflow.guidance.settings',
    version: 1,
    fields: {
      text: { type: 'string', required: true, maxLength: 2000 },
    },
  },
  placements: [
    { host: 'step-shell', region: 'main' },
    { host: 'step-shell', region: 'supporting' },
  ],
  layout: {
    widths: ['full', 'half'],
    minimumWidthPx: 160,
    minimumHeightPx: 64,
    responsive: 'stack-in-order',
  },
  instances: 'repeatable',
};
export const reviewRecordsBlock: BlockManifest = {
  provider: 'platform',
  code: 'workflow.review-records',
  version: 1,
  name: 'Review records',
  description:
    'The latest 25 review records across this workflow, subject to current access and field permissions.',
  category: 'Workflow',
  keywords: ['review', 'history', 'notes'],
  contextNeeds: ['workflowInstanceId'],
  reads: [
    {
      code: 'workflow.review.records',
      version: 1,
      dataContract: { code: 'workflow.review.records.page', version: 1 },
      parameters: {},
    },
  ],
  settingsSchema: {
    code: 'workflow.review-records.settings',
    version: 1,
    fields: {
      showNotes: { type: 'boolean', required: true },
    },
  },
  placements: [
    { host: 'step-shell', region: 'main' },
    { host: 'step-shell', region: 'supporting' },
  ],
  layout: {
    widths: ['full', 'half'],
    minimumWidthPx: 160,
    minimumHeightPx: 96,
    responsive: 'stack-in-order',
  },
  instances: 'single-per-workspace',
};
function visitBlock(
  code: string,
  name: string,
  description: string,
  read: string,
): BlockManifest {
  return {
    provider: 'platform',
    code,
    version: 1,
    name,
    description,
    category: 'Workflow',
    keywords: ['visit', 'staff', 'record'],
    contextNeeds: ['workflowInstanceId', 'stepOccurrenceId'],
    reads: [
      {
        code: read,
        version: 1,
        dataContract: { code: `${read}.page`, version: 1 },
        parameters: { occurrenceId: 'stepOccurrenceId' },
      },
    ],
    settingsSchema: { code: `${code}.settings`, version: 1, fields: {} },
    placements: [
      { host: 'step-shell', region: 'main' },
      { host: 'step-shell', region: 'supporting' },
    ],
    layout: {
      widths: ['full', 'half'],
      minimumWidthPx: 250,
      minimumHeightPx: 96,
      responsive: 'stack-in-order',
    },
    instances: 'single-per-workspace',
  };
}
export const informationBlock = visitBlock(
  'workflow.information',
  'Visit information',
  'Process, responsibility and connected-record information for the selected retained visit.',
  'workflow.step.information',
);
export const requirementsEvidenceBlock = visitBlock(
  'workflow.requirements-evidence',
  'Requirements and evidence',
  'Read-only checks and evidence references for the selected visit. Completion commands recheck current requirements.',
  'workflow.step.requirements-evidence',
);
export const activityBlock = visitBlock(
  'workflow.activity',
  'Visit activity',
  'Recorded activity for the selected visit, using the shared workflow history renderer.',
  'workflow.step.activity',
);
export const sharedNotesBlock = visitBlock(
  'workflow.shared-notes',
  'Shared notes',
  'Hearing-linked shared notes with recorded authors, permitted corrections and retained revision history.',
  'workflow.step.shared-notes',
);
export const noteTimelineBlock = visitBlock(
  'workflow.note-timeline',
  'Notes and activity',
  'Paged recorded notes and available workflow/module activity with links to their original sources.',
  'workflow.step.note-timeline',
);

export const deadlinesBlock: BlockManifest = {
  ...visitBlock(
    'workflow.deadlines',
    'Deadlines',
    'Selected recorded deadlines with their owning rule, calendar, date basis and current evaluated countdown.',
    'workflow.step.deadlines',
  ),
  // This new provider publishes a result contract; retained visit views keep their page contracts.
  reads: [
    {
      code: 'workflow.step.deadlines',
      version: 1,
      dataContract: { code: 'workflow.step.deadlines.result', version: 1 },
      parameters: { occurrenceId: 'stepOccurrenceId' },
    },
  ],
};

/** Additive recorded optional-stage display; existing saved block1 references remain unchanged. */
export const deadlinesBlockV2: BlockManifest = {
  ...visitBlock(
    'workflow.deadlines',
    'Deadlines',
    'Recorded deadlines with explicit warning and overdue-action presence, exact date evidence and the server countdown.',
    'workflow.step.deadlines',
  ),
  version: 2,
  reads: [
    {
      code: 'workflow.step.deadlines',
      version: 2,
      dataContract: { code: 'workflow.step.deadlines.result', version: 2 },
      parameters: { occurrenceId: 'stepOccurrenceId' },
    },
  ],
};

/** Every installed version, including retained deprecated versions. No component imports or reads. */
export const workflowBlockManifests: readonly BlockManifest[] = [
  guidanceBlock,
  reviewRecordsBlock,
  informationBlock,
  requirementsEvidenceBlock,
  activityBlock,
  sharedNotesBlock,
  noteTimelineBlock,
  deadlinesBlock,
  deadlinesBlockV2,
  documentsBlock,
  caseSnapshotBlock,
  ...domainBlockManifests,
  ...votingBlockManifests,
  ...schedulingBlockManifests,
  ...hearingCompletionManifests,
];

/** Exact presentation contracts supported by the staff workspace and authoring canvas. */
export const workflowWorkspaceLayouts = [
  {
    code: 'workspace.two-column.v1',
    contextVersion: 1,
    contextPlacements: ['side', 'below', 'hidden'],
    layout: TWO_COLUMN_WORKSPACE,
  },
] as const;
